'use client';
import { useState } from 'react';
import Image from 'next/image';
import { Shield } from 'lucide-react';
import { IconArrowNarrowRight } from '@tabler/icons-react';
import { cn } from '@/app/utils/cn';
import { SvgIcon } from '@/components/shared/SvgIcon';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';
import LinkNoPrefetch from '@/components/link-noprefetch';
import { getDotcmsImageURL } from '@/app/utils/getDotcmsImageURL';

const RADIUS_PERCENT = 32.5;
const SVG_RADIUS = 130;
const SVG_CENTER = 200;

function getNodePosition(index, total) {
    const angle = (index * (360 / total) - 90) * (Math.PI / 180);
    return {
        x: 50 + Math.cos(angle) * RADIUS_PERCENT,
        y: 50 + Math.sin(angle) * RADIUS_PERCENT
    };
}

function getSvgLineEnd(index, total) {
    const angle = (index * (360 / total) - 90) * (Math.PI / 180);
    return {
        x: Math.cos(angle) * SVG_RADIUS + SVG_CENTER,
        y: Math.sin(angle) * SVG_RADIUS + SVG_CENTER
    };
}

function getImageSrc(image, width = 600) {
    const identifier = image?.identifier || image?.fileAsset?.idPath;
    if (!identifier) return null;
    if (typeof identifier === 'string' && identifier.startsWith('/')) return identifier;
    return getDotcmsImageURL({ src: identifier, width, raw: true });
}

const descriptionRenderers = {
    paragraph: ({ children }) => (
        <p className="text-foreground/70 text-lg leading-relaxed">{children}</p>
    )
};

const sectionBodyRenderers = {
    paragraph: ({ children }) => (
        <p className="text-base leading-relaxed text-gray-600">{children}</p>
    )
};

function IndustriesDisplay(props) {
    const { industriesSections = [], industriesStats } = props;
    const stats = industriesStats?.stats || [];
    const total = industriesSections.length;
    const [active, setActive] = useState(0);
    const [hasInteracted, setHasInteracted] = useState(false);

    const selectSection = (i) => {
        setActive(i);
        setHasInteracted(true);
    };

    if (!total) return null;

    const section = industriesSections[active];
    const stat = stats[active];

    const hasAnySectionImage = industriesSections.some((s) =>
        getImageSrc(s?.altImage || s?._map?.altImage, 900)
    );
    const sectionUrl = section?.callToAction?.[0]?.url || section?.url || null;
    const sectionBody = section?.body?.json ?? section?.body ?? section?.content ?? null;

    return (
        <section className="overflow-hidden bg-transparent py-12 lg:py-20">
            <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-3 lg:gap-10">
                    {/* Wheel */}
                    <div className="relative mx-auto aspect-square w-full max-w-lg self-center">
                        <div className="absolute inset-0 rounded-full border border-gray-300/50" />
                        <div className="absolute inset-4 rounded-full border border-gray-300/30" />

                        <svg className="absolute inset-0 z-0 size-full" viewBox="0 0 400 400">
                            {Array.from({ length: total }).map((_, i) => {
                                const { x, y } = getSvgLineEnd(i, total);
                                const isActive = active === i;
                                return (
                                    <line
                                        key={i}
                                        x1={SVG_CENTER}
                                        y1={SVG_CENTER}
                                        x2={x}
                                        y2={y}
                                        className={
                                            isActive ? 'stroke-fuschia-600' : 'stroke-gray-300/40'
                                        }
                                        strokeWidth={isActive ? 2 : 1}
                                    />
                                );
                            })}
                        </svg>

                        {industriesSections.map((nodeSection, i) => {
                            const { x, y } = getNodePosition(i, total);
                            const isActive = active === i;
                            const nodeIcon = nodeSection?.icon;
                            const nodeImage = getImageSrc(nodeSection?.image, 200);
                            const nodeLabel = nodeSection?.title;

                            return (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => selectSection(i)}
                                    aria-label={nodeLabel || `Industry ${i + 1}`}
                                    aria-pressed={isActive}
                                    className="group absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full"
                                    style={{ left: `${x}%`, top: `${y}%` }}>
                                    {!hasInteracted && !isActive && (
                                        <span
                                            aria-hidden
                                            className="pointer-events-none absolute inset-0 rounded-full bg-fuschia-600/15 ![animation-duration:3s] motion-safe:animate-ping"
                                        />
                                    )}
                                    <div
                                        className={cn(
                                            'relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-white transition-all duration-500 lg:h-20 lg:w-20',
                                            isActive
                                                ? 'shadow-xl shadow-fuschia-600/20 ring-4 ring-fuschia-600 ring-offset-2 ring-offset-white'
                                                : 'ring-2 ring-gray-300 hover:scale-105 hover:ring-fuschia-600/50'
                                        )}>
                                        {nodeImage ? (
                                            <div
                                                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                                                style={{ backgroundImage: `url(${nodeImage})` }}
                                            />
                                        ) : (
                                            nodeIcon && (
                                                <SvgIcon
                                                    icon={nodeIcon}
                                                    size={28}
                                                    className="relative size-7 shrink-0 text-gray-500"
                                                />
                                            )
                                        )}
                                    </div>
                                </button>
                            );
                        })}

                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                            <div className="flex size-20 items-center justify-center rounded-full bg-gradient-to-br from-fuschia-600 to-fuschia-700 shadow-lg shadow-fuschia-600/30 lg:size-24">
                                <Shield className="text-white lg:size-10" />
                            </div>
                        </div>
                    </div>

                    {/* Stat card — only rendered when the active section has a
                        matching stat (pairs by index). If missing, the middle
                        column is dropped rather than shown empty. */}
                    {stat && (
                        <div className="flex h-full flex-col items-center justify-center rounded-2xl bg-gray-100 px-4 py-6 text-center sm:px-6 sm:py-8">
                            <div
                                className="mb-1 font-extrabold text-fuschia-700"
                                style={{ fontSize: 'clamp(2.5rem, 5.5vw, 3.75rem)' }}>
                                {stat.stat}
                            </div>
                            {stat.statlabel && (
                                <p className="mb-4 text-xs uppercase tracking-[0.15em] text-gray-500">
                                    {stat.statlabel}
                                </p>
                            )}
                            {stat.description && (
                                <DotBlockEditor
                                    customRenderers={descriptionRenderers}
                                    blocks={stat.description}
                                />
                            )}
                        </div>
                    )}

                    {/* Section card */}
                    <div className="flex flex-col">
                        {hasAnySectionImage ? (
                            <div className="relative mb-6 aspect-[16/9] w-full overflow-hidden rounded-2xl bg-gray-100 shadow-lg shadow-fuschia-600/10 ring-1 ring-fuchsia-100">
                                {industriesSections.map((item, i) => {
                                    const itemImage = getImageSrc(
                                        item?.altImage || item?._map?.altImage,
                                        900
                                    );
                                    if (!itemImage) return null;
                                    return (
                                        <div
                                            key={item.identifier || i}
                                            className={cn(
                                                'absolute inset-0 bg-cover bg-center transition-all duration-700',
                                                active === i
                                                    ? 'scale-100 opacity-100'
                                                    : 'scale-105 opacity-0'
                                            )}
                                            style={{ backgroundImage: `url(${itemImage})` }}
                                        />
                                    );
                                })}
                                {section?.label && (
                                    <div className="absolute bottom-4 left-4 rounded-full bg-fuschia-700 px-3 py-1 text-xs font-semibold text-white">
                                        {section.label}
                                    </div>
                                )}
                            </div>
                        ) : (
                            section?.label && (
                                <span className="mb-3 inline-flex w-fit rounded-full bg-fuschia-700 px-3 py-1 text-xs font-semibold text-white">
                                    {section.label}
                                </span>
                            )
                        )}

                        <div className="space-y-4">
                            {section?.title && (
                                <h3 className="text-2xl font-bold text-gray-900 lg:text-3xl">
                                    {section.title}
                                </h3>
                            )}

                            {sectionBody && (
                                <DotBlockEditor
                                    blocks={sectionBody}
                                    customRenderers={sectionBodyRenderers}
                                />
                            )}

                            {sectionUrl && (
                                <LinkNoPrefetch
                                    href={sectionUrl}
                                    className="group/btn hover:bg-fuschia-50 mt-2 inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 transition-colors hover:border-fuschia-600">
                                    <span>Learn more</span>
                                    <IconArrowNarrowRight className="transition-transform group-hover/btn:translate-x-1" />
                                </LinkNoPrefetch>
                            )}
                        </div>

                        <div className="mt-8 flex items-center gap-2">
                            {Array.from({ length: total }).map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => selectSection(i)}
                                    aria-label={`Show industry ${i + 1}`}
                                    aria-pressed={active === i}
                                    className={cn(
                                        'h-2 rounded-full transition-all duration-300',
                                        active === i
                                            ? 'w-8 bg-fuschia-600'
                                            : 'w-2 bg-gray-300 hover:bg-fuschia-600/50'
                                    )}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default IndustriesDisplay;
